/**
 * Module Dependencies
 */

import { get } from 'safe-prop'
import trim_uid from 'trim-uid'
import sorter from 'utils/sorter'
import uniqs from 'utils/uniqs'
import not_in from 'utils/not-in'

/**
 * Action Types
 */

import {
  LOAD_USER_RECEIVE,
  PROFILE_UPDATE_REQUEST,
  PROFILE_UPDATE_RECEIVE,
  API_KEY_REQUEST,
  API_KEY_RECEIVE,
  API_KEY_FAILURE,
  API_USAGE_REQUEST,
  API_USAGE_RECEIVE,
  API_USAGE_FAILURE,
  RESEND_EMAIL_VERIFICATION_REQUEST,
  RESEND_EMAIL_VERIFICATION_RECEIVE,
  RESEND_EMAIL_VERIFICATION_FAILURE,
  PROGRESS_UPDATE_RECEIVE,
  LOAD_ADVOCATE_REQUEST,
  LOAD_ADVOCATE_RECEIVE,
  LOAD_ADVOCATE_FAILURE,
  LOAD_AFFILIATE_REQUEST,
  LOAD_AFFILIATE_RECEIVE,
  LOAD_AFFILIATE_FAILURE,
  LOAD_AFFILIATE_RESOURCE_REQUEST,
  LOAD_AFFILIATE_RESOURCE_RECEIVE,
  LOAD_AFFILIATE_RESOURCE_FAILURE,
  VERIFY_AFFILIATE_REQUEST,
  VERIFY_AFFILIATE_RECEIVE,
  VERIFY_AFFILIATE_FAILURE,
  UPDATE_AFFILIATE_PAYPAL_RECEIVE,
  SEND_INVITE_REQUEST,
  SEND_INVITE_RECEIVE,
  SEND_INVITE_FAILURE,
  RESEND_INVITE_REQUEST,
  RESEND_INVITE_RECEIVE,
  RESEND_INVITE_FAILURE,
  USER_SET_EMAIL_VERIFIED,
  ADD_USER_COURSE,
  UPDATE_USER_WATCHLIST_PRICE_TARGET,
  UPDATE_COMPANIES_WITH_MODELS,
  UPDATE_USER_SCREENS,
  UPDATE_USER_PLAN,
  SET_USER_DATA
} from 'redux-types/user'

import {
  VIEW_CREATE_REQUEST,
  VIEW_CREATE_RECEIVE,
  VIEW_CREATE_FAILURE,
  VIEW_REORDER_REQUEST,
  VIEW_RENAME_REQUEST,
  VIEW_UPDATE_COLUMNS_REQUEST,
  VIEW_DELETE_REQUEST
} from 'redux-types/view'

import {
  WATCHLIST_ADD_TICKER_REQUEST,
  WATCHLIST_ADD_TICKERS_RECEIVE,
  WATCHLIST_ADD_TICKERS_REQUEST,
  WATCHLIST_CREATE_LIST_FAILURE,
  WATCHLIST_CREATE_LIST_RECEIVE,
  WATCHLIST_CREATE_LIST_REQUEST, WATCHLIST_DELETE_LIST_REQUEST,
  WATCHLIST_REMOVE_TICKER_REQUEST,
  WATCHLIST_REMOVE_TICKERS_REQUEST,
  WATCHLIST_RENAME_LIST_REQUEST
} from 'redux-types/watchlist'

/**
 * Environment
 */

const URL = CONFIG.publicUrl

/**
 * Reducer
 */

export default function reducer (state = false, action) {
  switch (action.type) {
    case LOAD_USER_RECEIVE:
      return {
        ...state,
        ...action.user
      }

    case SET_USER_DATA:
      return action.data

    case PROFILE_UPDATE_REQUEST:
      const _u = {}
      const { email_verified, email } = state
      if (email_verified && get(action.update, 'email')) {
        _u.email_verified = get(action.update, 'email') === email
        _u.email = action.update.email
      } else if (get(action.update, 'views')) {
        _u.views = action.update.views
      }

      return {
        ...state,
        ..._u
      }

    case USER_SET_EMAIL_VERIFIED:
      return {
        ...state,
        email_verified: true
      }

    case PROFILE_UPDATE_RECEIVE:
      return {
        ...state,
        ...action.update
      }

    case PROGRESS_UPDATE_RECEIVE:
      return {
        ...state,
        progress: action.progress
      }

    case API_KEY_REQUEST:
      return {
        ...state,
        api_key_status: {
          loading: true,
          error: false
        }
      }

    case API_KEY_RECEIVE:
      return {
        ...state,
        api_key: action.api_key,
        api_key_status: {
          loading: false,
          error: false
        }
      }

    case API_KEY_FAILURE:
      return {
        ...state,
        api_key_status: {
          loading: false,
          error: true
        }
      }

    case API_USAGE_REQUEST:
      return {
        ...state,
        api_usage: {
          loading: true,
          error: false
        }
      }

    case API_USAGE_RECEIVE:
      return {
        ...state,
        api_usage: {
          ...action.api_usage,
          as_of: new Date(),
          loading: false,
          error: false
        }
      }

    case API_USAGE_FAILURE:
      return {
        ...state,
        api_usage: {
          loading: false,
          error: true
        }
      }

    case RESEND_EMAIL_VERIFICATION_REQUEST:
      return {
        ...state,
        verification_email: {
          loading: true,
          loaded: false,
          error: false
        }
      }

    case RESEND_EMAIL_VERIFICATION_RECEIVE:
      return {
        ...state,
        verification_email: {
          loading: false,
          loaded: true,
          error: false
        }
      }

    case RESEND_EMAIL_VERIFICATION_FAILURE:
      return {
        ...state,
        verification_email: {
          loading: false,
          loaded: false,
          error: true
        }
      }

    case LOAD_ADVOCATE_REQUEST:
      return {
        ...state,
        advocacy: {
          ...state.advocacy,
          loading: true,
          recruits: [],
          token: null,
          click_count: 0,
          link: null,
          total_rewards: 0,
          total_emails: 0
        }
      }

    case LOAD_ADVOCATE_RECEIVE:
      const { advocate } = action
      return {
        ...state,
        advocacy: {
          ...state.advocacy,
          loading: false,
          recruits: advocate.recruits,
          total_rewards: _total_rewards(advocate.recruits),
          total_emails: _total_emails(advocate.recruits),
          token: advocate.token,
          link: _create_share_link(advocate.token),
          click_count: advocate.click_count
        }
      }

    case LOAD_ADVOCATE_FAILURE:
      return {
        ...state,
        advocacy: {
          ...state.advocacy,
          loading: false,
          error: true
        }
      }

    case LOAD_AFFILIATE_REQUEST:
      return {
        ...state,
        affiliate: {
          loading: true,
          error: false
        }
      }

    case LOAD_AFFILIATE_RECEIVE:
      return {
        ...state,
        affiliate: {
          loading: false,
          error: false,
          ...action.affiliate
        }
      }

    case LOAD_AFFILIATE_FAILURE:
      return {
        ...state,
        affiliate: {
          loading: false,
          error: true
        }
      }

    case LOAD_AFFILIATE_RESOURCE_REQUEST:
      const current = state.affiliate[action.resource].data
      return {
        ...state,
        affiliate: {
          ...state.affiliate,
          [action.resource]: {
            ...state.affiliate[action.resource],
            loading_more: true,
            error: false,
            data: action.refresh ? [] : current
          }
        }
      }

    case LOAD_AFFILIATE_RESOURCE_RECEIVE:
      const prior = state.affiliate[action.resource].data
      return {
        ...state,
        affiliate: {
          ...state.affiliate,
          [action.resource]: {
            ...action.payload,
            loading_more: false,
            error: false,
            data: prior.concat(action.payload.data)
          }
        }
      }

    case LOAD_AFFILIATE_RESOURCE_FAILURE:
      return {
        ...state,
        affiliate: {
          ...state.affiliate,
          [action.resource]: {
            ...state.affiliate[action.resource],
            loading_more: false,
            error: true
          }
        }
      }

    case VERIFY_AFFILIATE_REQUEST:
      return {
        ...state,
        affiliate: {
          loading: true,
          error: false
        }
      }

    case VERIFY_AFFILIATE_RECEIVE:
      return {
        ...state,
        affiliate: {
          loading: false,
          error: false,
          ...action.affiliate
        }
      }

    case VERIFY_AFFILIATE_FAILURE:
      return {
        ...state,
        affiliate: {
          loading: false,
          error: true
        }
      }

    case UPDATE_AFFILIATE_PAYPAL_RECEIVE:
      return {
        ...state,
        affiliate: {
          ...state.affiliate,
          ...action.affiliate
        }
      }

    case SEND_INVITE_REQUEST:
      return {
        ...state,
        advocacy: {
          ...state.advocacy,
          sending: action.email
        }
      }

    case SEND_INVITE_RECEIVE:
      const update = action.advocate
      return {
        ...state,
        advocacy: {
          ...state.advocacy,
          sending: false,
          recruits: update.recruits,
          total_rewards: _total_rewards(update.recruits),
          total_emails: _total_emails(update.recruits),
          token: update.token,
          link: _create_share_link(update.token),
          click_count: update.click_count
        }
      }

    case SEND_INVITE_FAILURE:
      return {
        ...state,
        advocacy: {
          ...state.advocacy,
          sending: false
        }
      }

    case RESEND_INVITE_REQUEST:
      return {
        ...state,
        advocacy: {
          ...state.advocacy,
          sending: action.email
        }
      }

    case RESEND_INVITE_RECEIVE:
      const recruits = get(state, 'advocacy.recruits') || []
      return {
        ...state,
        advocacy: {
          ...state.advocacy,
          sending: false,
          recruits: recruits.map((o) => {
            if (o.email === action.email) {
              return {
                ...o,
                email_date: new Date().toISOString()
              }
            }
            return o
          })
        }
      }

    case RESEND_INVITE_FAILURE:
      return {
        ...state,
        advocacy: {
          ...state.advocacy,
          sending: false
        }
      }

    case ADD_USER_COURSE:
      return {
        ...state,
        courses: state.courses
          .concat(action.item_id)
      }

    case UPDATE_USER_WATCHLIST_PRICE_TARGET:
      return {
        ...state,
        watchlists: state.watchlists.map((watchlist) => {
          return {
            ...watchlist,
            holdings: watchlist.holdings.map((o) => {
              if (o.full_ticker === action.full_ticker) {
                return {
                  ...o,
                  price_target: action.target
                }
              }
              return o
            })
          }
        })
      }

    case WATCHLIST_CREATE_LIST_REQUEST: {
      return {
        ...state,
        watchlists: state.watchlists.concat({
          slug: action.slug,
          name: action.name,
          holdings: action.holdings
        })
      }
    }

    case WATCHLIST_CREATE_LIST_RECEIVE:
      return {
        ...state,
        watchlists: update_watchlist({
          user: state,
          list_id: action.slug,
          name: action.name,
          holdings: action.holdings
        })
      }

    case WATCHLIST_CREATE_LIST_FAILURE:
      return {
        ...state,
        watchlists: state.watchlists
          .filter((o) => o.slug !== action.slug)
      }

    case WATCHLIST_RENAME_LIST_REQUEST:
      return {
        ...state,
        watchlists: update_watchlist({
          user: state,
          list_id: action.list_id,
          name: action.name
        })
      }

    case WATCHLIST_ADD_TICKER_REQUEST:
      return {
        ...state,
        watchlists: add_to_list({
          user: state,
          list_id: action.list_id,
          holdings: [action.asset]
        })
      }

    case WATCHLIST_ADD_TICKERS_REQUEST:
      return {
        ...state,
        watchlists: add_to_list({
          user: state,
          list_id: action.list_id,
          holdings: action.holdings
        })
      }

    case WATCHLIST_ADD_TICKERS_RECEIVE:
      return {
        ...state,
        watchlists: update_price_targets({
          user: state,
          list_id: action.list_id,
          holdings: action.holdings
        })
      }

    case WATCHLIST_REMOVE_TICKER_REQUEST:
      return {
        ...state,
        watchlists: remove_from_list({
          user: state,
          list_id: action.list_id,
          tickers: [action.full_ticker]
        })
      }

    case WATCHLIST_REMOVE_TICKERS_REQUEST:
      return {
        ...state,
        watchlists: remove_from_list({
          user: state,
          list_id: action.list_id,
          tickers: action.tickers
        })
      }

    case WATCHLIST_DELETE_LIST_REQUEST:
      return {
        ...state,
        watchlists: state.watchlists.filter((o) => o.slug !== action.list_id)
      }

    case UPDATE_COMPANIES_WITH_MODELS: {
      return {
        ...state,
        companies_with_models: relevant_companies({
          charts: action.charts,
          models: action.models,
          companies_with_models: action.companies_with_models
        })
      }
    }

    case UPDATE_USER_SCREENS: {
      return {
        ...state,
        screens: action.screens
      }
    }

    case UPDATE_USER_PLAN: {
      return {
        ...state,
        plan: {
          ...state.plan,
          ...action.plan
        }
      }
    }

    case VIEW_CREATE_REQUEST:
      return {
        ...state,
        views: state.views.concat({
          slug: action.slug,
          name: action.name,
          columns: action.columns
        })
      }

    case VIEW_CREATE_RECEIVE:
      return {
        ...state,
        views: update_view({
          user: state,
          view_id: action.view.slug,
          _id: action.view._id
        })
      }

    case VIEW_CREATE_FAILURE:
      return {
        ...state,
        views: state.views
          .filter((o) => o.slug !== action.slug)
      }

    case VIEW_REORDER_REQUEST:
      const vlookup = state.views.reduce((p, o) => {
        p[o.slug] = o
        return p
      }, {})
      return {
        ...state,
        views: action.order.map((s) => vlookup[s])
      }

    case VIEW_RENAME_REQUEST:
      return {
        ...state,
        views: update_view({
          user: state,
          view_id: action.view_id,
          name: action.name
        })
      }

    case VIEW_UPDATE_COLUMNS_REQUEST:
      return {
        ...state,
        views: update_view({
          user: state,
          view_id: action.view_id,
          columns: action.columns
        })
      }

    case VIEW_DELETE_REQUEST:
      return {
        ...state,
        views: state.views.filter((o) => {
          return o.slug !== action.view_id
        })
      }

    default:
      return state
  }
}

export function _total_rewards (recruits = []) {
  const total = recruits.reduce((p, o) => {
    return p + (o.converted ? o.reward : 0)
  }, 0)
  return total / 100
}

export function _total_emails (recruits = []) {
  return recruits.reduce((p, o) => {
    return p + (o.campaign === 'email' ? 1 : 0)
  }, 0)
}

export function _create_share_link (token) {
  return `${URL}?r=${token}`
}

export function update_view ({ user, view_id, ...other }) {
  return user.views.map((o) => {
    if (o.slug === view_id) {
      return {
        ...o,
        ...other
      }
    }
    return o
  })
}

export function relevant_companies ({ companies_with_models, models, charts }) {
  const combined = models.concat(charts)
  const cache = {}
  const valid_tickers = combined.reduce((p, { ticker }) => {
    const _ticker = trim_uid(ticker)

    if (!cache[_ticker]) {
      p.push(_ticker)
      cache[_ticker] = true
    }
    return p
  }, [])
  const lookup = companies_with_models.reduce((p, o) => {
    o.short_name = o.short_name || o.ticker
    p[trim_uid(o.ticker)] = o
    return p
  }, {})
  return valid_tickers.map((ticker) => lookup[ticker])
    .filter(Boolean)
    .sort(sorter('short_name'))
}

export function update_price_targets ({ user, list_id, holdings }) {
  const lookup = holdings.reduce((p, o) => { p[o.ticker] = o; return p }, {})
  return user.watchlists.map((o) => {
    if (o.slug === list_id) {
      return {
        ...o,
        holdings: o.holdings.map((s) => {
          if (lookup[s.ticker]) {
            return {
              ...s,
              ...lookup[s.ticker]
            }
          }
          return s
        })
      }
    }
    return o
  })
}

export function update_watchlist ({ user, list_id, holdings = [], ...other }) {
  return user.watchlists.map((o) => {
    if (o.slug === list_id) {
      const update = { ...o, ...other }
      if (holdings.length) update.holdings = holdings
      return update
    }
    return o
  })
}

export function add_to_list ({ user, list_id, holdings }) {
  return user.watchlists.map((o) => {
    if (o.slug === list_id) {
      return {
        ...o,
        holdings: uniqs(o.holdings.concat(holdings), 'ticker')
      }
    }
    return o
  })
}

export function remove_from_list ({ user, list_id, tickers }) {
  const keep = not_in(tickers)
  return user.watchlists.map((o) => {
    if (o.slug === list_id) {
      return {
        ...o,
        holdings: o.holdings.filter((o) => keep(o.full_ticker))
      }
    }
    return o
  })
}
